import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { SubscriptionNumber } from '@web/shared/module/shop';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, Observable, switchMap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AlertsComponent,
  AlertsService,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { HttpErrorService } from '@web/common';
import { AdminSubscriptionBillingAddressFormComponent } from '../admin-subscription-billing-address-form/admin-subscription-billing-address-form.component';
import { AdminSubscriptionBillingAddressForm } from '../../models/admin-subscription-billing-address-form';
import { AdminSubscriptionDataSourceService } from '../../services/admin-subscription-data-source.service';
import { Guid } from '@web/utils';

@Component({
  selector: 'app-admin-subscription-billing-address-edit',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertsComponent,
    SpinnerFilledComponent,
    AdminSubscriptionBillingAddressFormComponent,
  ],
  templateUrl: './admin-subscription-billing-address-edit.component.html',
  styleUrl: './admin-subscription-billing-address-edit.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AlertsService],
})
export class AdminSubscriptionBillingAddressEditComponent {
  public readonly id = input.required<Guid>();
  public readonly submitEvent = output<void>();

  protected readonly pending = signal(false);
  protected readonly id$ = this.idSource();
  protected readonly data$ = this.dataSource();

  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(AdminSubscriptionDataSourceService);
  private readonly httpError = inject(HttpErrorService);
  private readonly alerts = inject(AlertsService);

  public handleSubmitClickEvent(
    data: AdminSubscriptionBillingAddressForm,
  ): void {
    this.markAsPending();

    this.subscriptionDataSource
      .updateBillingAddressByFormData({
        id: this.id(),
        data,
      })
      .pipe(finalize(() => this.markAsResolved()))
      .subscribe({
        next: () => this.submitEvent.emit(),
        error: (response) => this.showFailureAlertByResponse(response),
      });
  }

  private markAsPending(): void {
    this.pending.set(true);
  }

  private markAsResolved(): void {
    this.pending.set(false);
  }

  private idSource(): Observable<SubscriptionNumber> {
    return toObservable(this.id);
  }

  private dataSource(): Observable<AdminSubscriptionBillingAddressForm> {
    return this.id$.pipe(
      switchMap((id) =>
        this.subscriptionDataSource.fetchBillingAddressFormById(id),
      ),
    );
  }

  private showFailureAlertByResponse(response: HttpErrorResponse): void {
    const messages = this.httpError.extract(response).messages();

    this.alerts.showMultiple((builder) =>
      messages.map((message) => builder.buildFailure(message)),
    );
  }
}
